import { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Reveal, Tag } from "@/components/System";
import { WORKFLOW_STAGES } from "@/data/content";

const Workflow = () => {
    const [active, setActive] = useState(0);

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) return;
        const t = setInterval(() => setActive((a) => (a + 1) % WORKFLOW_STAGES.length), 900);
        return () => clearInterval(t);
    }, []);

    return (
        <div>
            <Seo path="/workflow" />
            <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
                <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl">
                    <div className="vm-micro mb-6 flex items-center gap-3">AGENTVM://WORKFLOW <Tag tone="concept">SIMULATION</Tag></div>
                    <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                        THE LIFE OF A
                        <br />
                        <span className="text-[#00D9FF] vm-glow">VERIFIABLE RUN.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                        Nine stages, one luminous packet. Watch a unit of work travel from creation to onchain record —
                        each stage activates as the packet passes through it.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-4 pb-28 sm:px-6" data-testid="workflow-pipeline">
                <Reveal>
                    <div className="relative">
                        <div className="absolute bottom-4 left-[27px] top-4 w-px bg-[rgba(0,217,255,0.18)]" aria-hidden="true">
                            <div className="vm-packet absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-[#00D9FF] shadow-[0_0_16px_rgba(0,217,255,0.9)]" />
                        </div>
                        <div className="flex flex-col gap-2.5">
                            {WORKFLOW_STAGES.map((s, i) => {
                                const isActive = i === active;
                                const passed = i < active;
                                return (
                                    <div
                                        key={s.id}
                                        data-testid={`stage-${s.id}`}
                                        className={`relative z-10 flex items-center gap-5 border px-4 py-4 transition-all duration-500 ${
                                            isActive
                                                ? "border-[#00D9FF]/60 bg-[#00D9FF]/[0.07] shadow-[0_0_36px_rgba(0,217,255,0.1)]"
                                                : passed
                                                  ? "border-[rgba(0,217,255,0.22)] bg-[#080F18]/60"
                                                  : "border-[rgba(0,217,255,0.08)] bg-[#080F18]/30"
                                        }`}
                                    >
                                        <span
                                            className={`flex h-7 w-7 shrink-0 items-center justify-center border font-mono text-[9px] transition-colors duration-500 ${
                                                isActive
                                                    ? "border-[#00D9FF] text-[#00D9FF]"
                                                    : passed
                                                      ? "border-[#1688FF]/50 text-[#1688FF]"
                                                      : "border-[rgba(0,217,255,0.2)] text-[#B8C7D9]/50"
                                            }`}
                                        >
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <div className="min-w-0">
                                            <div className={`font-mono text-xs tracking-[0.2em] sm:text-sm ${isActive ? "text-[#F5F9FF]" : "text-[#B8C7D9]"}`}>
                                                {s.name}
                                            </div>
                                            <p className="mt-1 truncate text-xs text-[#B8C7D9]/60 sm:whitespace-normal">{s.desc}</p>
                                        </div>
                                        <span className={`vm-micro-dim ml-auto hidden sm:block ${isActive ? "text-[#00D9FF]" : ""}`}>
                                            {isActive ? "PROCESSING" : passed ? "COMPLETE" : "QUEUED"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Workflow;
